import { ColDef, ColGroupDef, ColumnApi, GridApi, ValueFormatterParams } from '@ag-grid-community/core'
import { AgGridReact } from '@ag-grid-community/react'
import { AllModules } from '@ag-grid-enterprise/all-modules'
import {
  BreadcrumbProps,
  Button,
  Card,
  Col,
  message,
  PageHeader,
  Popconfirm,
  Progress,
  Row,
  Space,
  Tooltip,
  Typography,
} from 'antd'
import { LabeledValue } from 'antd/lib/select'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { first, isEmpty, isEqual, isNil, pick, pickBy } from 'lodash'
import React, { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'
import {
  MarkupRuleWithDetail,
  Organization,
  useDeleteMarkupRuleMutation,
  useOrganizationsWithTopParentQuery,
  useUpsertMarkupRulesMutation,
} from '../../../api'
import { LaunchDarklyFeatures } from '../../../common/constants'
import { stringCaseInsensitiveComparator } from '../../../common/util'
import { breadcrumbItemRender, percentageFormatter } from '../../../utils'
import { getNumericCellEditor } from '../../Activate/OpportunityCreate/PricingTarget/NumericCellEditor'
import { AgFacilitySelector } from './components/AgFacilitySelector'
import { AgMarketBasketSelector } from './components/AgMarketBasketSelector'
import {
  AgMultipleNormalizedManufacturerSearch,
  AgNormalizedSupplierSearch,
} from './components/AgNormalizedManufacturerSearch'
import { AgUnspscCommoditySelector } from './components/AgUnspscCommoditySelector'
import { useLoadMarkupRules } from './hooks/useLoadMarkupRules'

const RULE_FIELDS = ['topParentId', 'facilityId', 'vendor', 'manufacturer', 'unspsc', 'marketBasket']

export const MarkupRules: React.FC = () => {
  const { customerId } = useParams<{ customerId: string }>()
  const navigate = useNavigate()
  const featureFlags = useFlags()
  const markupRulesFeature = featureFlags[LaunchDarklyFeatures.markupRules]

  const [selectedRows, setSelectedRows] = useState<MarkupRuleWithDetail[]>([])
  const gridRef = useRef<AgGridReact & { api: GridApi; columnApi: ColumnApi }>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [markupRuleStackBeforeEdit, setMarkupRuleStackBeforeEdit] = useState<MarkupRuleWithDetail[]>([])
  const [editingItem, setEditingItem] = useState<Partial<MarkupRuleWithDetail>>()

  const { loading, progress } = useLoadMarkupRules(gridRef, true)
  const [deleteMarkupRule, { loading: deleting }] = useDeleteMarkupRuleMutation()
  const [upsertMarkupRule, { loading: updating }] = useUpsertMarkupRulesMutation()
  const { data: organizationsWithTopParent } = useOrganizationsWithTopParentQuery()

  useEffect(() => {
    if (!markupRulesFeature) {
      navigate(`/manage/customer/${customerId}`, { replace: true })
    }
  }, [markupRulesFeature, navigate, customerId])

  const handleDelete = useCallback(() => {
    deleteMarkupRule({ variables: { ids: selectedRows.map(i => i.id) } })
      .then(() => {
        gridRef.current?.api.applyTransaction({
          remove: selectedRows.map(i => ({ id: i.id })),
        })
        setSelectedRows([])
        message.success('Markup rules deleted successfully')
      })
      .catch(() => {
        message.error('Failed to delete markup rules')
      })
  }, [selectedRows, deleteMarkupRule])

  const handleCreate = useCallback(() => {
    gridRef.current?.api.applyTransactionAsync(
      {
        add: [{ id: `new-${Date.now()}`, markupPercent: 0 }],
      },
      transactionRes => {
        setTimeout(() => {
          if (!isNil(transactionRes.add[0]?.rowIndex)) {
            gridRef.current?.api.startEditingCell({
              rowIndex: transactionRes.add[0].rowIndex,
              colKey: 'markupPercent',
            })
          }
        }, 50)
      },
    )
  }, [gridRef])

  const columns = useMemo(
    () => genColumns(organizationsWithTopParent?.organizationsWithTopParent || []),
    [organizationsWithTopParent],
  )

  const breadcrumbOptions: BreadcrumbProps = {
    itemRender: breadcrumbItemRender,
    routes: [
      {
        path: `/manage/customer/${customerId}`,
        breadcrumbName: 'Manage Customer',
      },
      {
        path: '',
        breadcrumbName: 'Markup Rules',
      },
    ],
  }

  return (
    <StyledLayout>
      <Helmet>
        <title>Markup Rules</title>
      </Helmet>
      <PageHeader title="Markup Rules" breadcrumb={breadcrumbOptions} />
      <Wrapper className="content-fixed-wide">
        <Card>
          <Row>
            <Col span={24}>
              <Space>
                <Button disabled={isEditing || updating} onClick={handleCreate}>
                  Create Markup Rule
                </Button>
                <Popconfirm
                  title={`Are you sure you want to delete the ${
                    selectedRows.length > 1 ? selectedRows.length + ' ' : ''
                  }selected rule${selectedRows.length === 1 ? '' : 's'}?`}
                  onConfirm={handleDelete}>
                  <Button disabled={!selectedRows.length || isEditing || updating} loading={deleting} danger>
                    Remove {selectedRows.length} Item{selectedRows.length > 1 ? 's' : ''}
                  </Button>
                </Popconfirm>
              </Space>
            </Col>
          </Row>
          <Row style={{ marginTop: '16px' }}>
            <Col span={24}>
              {loading && <Progress percent={progress} />}
              <div style={{ display: 'flex' }}>
                <EditingAgGridContext.Provider value={{ editingItem, onChangeEditingItem: setEditingItem }}>
                  <GridWrapper className="ag-theme-balham">
                    <AgGridReact
                      ref={gridRef}
                      editType="fullRow"
                      columnDefs={columns}
                      defaultColDef={{
                        resizable: true,
                      }}
                      components={{
                        floatEditor: getNumericCellEditor('float'),
                      }}
                      modules={AllModules}
                      getRowId={r => r.data.id}
                      rowSelection="multiple"
                      groupSelectsChildren
                      onSelectionChanged={({ api }) => {
                        setSelectedRows(api.getSelectedRows())
                      }}
                      isRowSelectable={({ data }) => {
                        return !data?.id?.startsWith('new-')
                      }}
                      enableGroupEdit={false}
                      onRowDataUpdated={({ columnApi }) => {
                        columnApi.autoSizeAllColumns()
                      }}
                      onFirstDataRendered={({ columnApi }) => {
                        columnApi.autoSizeAllColumns()
                      }}
                      onRowEditingStarted={({ data: v }) => {
                        setIsEditing(true)
                        setMarkupRuleStackBeforeEdit(old => [...old, { ...v }])
                        setEditingItem(v)
                      }}
                      onRowEditingStopped={({ data: v, node }) => {
                        setIsEditing(false)
                        if (
                          updating ||
                          (v?.id?.startsWith('new-') &&
                            isEqual(v, markupRuleStackBeforeEdit[markupRuleStackBeforeEdit.length - 1])) ||
                          isEmpty(
                            pickBy(v, (value, key) => !isNil(value) && [...RULE_FIELDS, 'markupPercent'].includes(key)),
                          )
                        ) {
                          gridRef.current?.api.deselectNode(node)
                          gridRef.current?.api.applyTransaction({
                            remove: [node],
                          })
                          setMarkupRuleStackBeforeEdit([])
                        }

                        if (
                          !v?.id?.startsWith('new-') &&
                          isEqual(v, markupRuleStackBeforeEdit[markupRuleStackBeforeEdit.length - 1])
                        ) {
                          gridRef.current?.api.applyTransaction({
                            update: [first(markupRuleStackBeforeEdit)],
                          })
                          setMarkupRuleStackBeforeEdit([])
                        }
                        setEditingItem(undefined)
                      }}
                      onRowValueChanged={({ data: v, node, rowIndex }) => {
                        gridRef.current?.api.showLoadingOverlay()
                        if (
                          isEmpty(pickBy(v, (value, key) => !isNil(value) && RULE_FIELDS.includes(key))) &&
                          v?.markupPercent
                        ) {
                          message.error('Please define a rule for this row')
                          rowIndex !== null &&
                            gridRef.current?.api.startEditingCell({
                              rowIndex,
                              colKey: 'topParentId',
                            })
                          gridRef.current?.api.hideOverlay()
                          return
                        }
                        if (isNil(v?.markupPercent) || v.markupPercent < 0 || v.markupPercent > 100) {
                          message.error('Please enter a Markup Percentage between 0 and 100')
                          rowIndex !== null &&
                            gridRef.current?.api.startEditingCell({
                              rowIndex,
                              colKey: 'markupPercent',
                            })
                          gridRef.current?.api.hideOverlay()
                          return
                        }
                        if (!v?.vendor) {
                          message.error('Please select a Vendor')
                          rowIndex !== null &&
                            gridRef.current?.api.startEditingCell({
                              rowIndex,
                              colKey: 'vendor',
                            })
                          gridRef.current?.api.hideOverlay()
                          return
                        }
                        if (!v?.topParentId) {
                          message.error('Please select a Top Parent')
                          rowIndex !== null &&
                            gridRef.current?.api.startEditingCell({
                              rowIndex,
                              colKey: 'topParentId',
                            })
                          gridRef.current?.api.hideOverlay()
                          return
                        }
                        const isAddingNew = v?.id?.startsWith('new-')
                        upsertMarkupRule({
                          variables: {
                            input: [
                              {
                                id: isAddingNew ? undefined : v?.id,
                                ...pick(v, RULE_FIELDS),
                                markupPercent: v?.markupPercent ? Number(v?.markupPercent) : 0,
                              },
                            ],
                          },
                        })
                          .then(res => {
                            if (v?.id && !v?.id?.startsWith('new-')) {
                              gridRef.current?.api.applyTransaction({
                                update: res.data?.upsertMarkupRules,
                              })
                            } else {
                              if (res.data?.upsertMarkupRules) {
                                gridRef.current?.api.applyTransaction({
                                  add: res.data?.upsertMarkupRules,
                                  remove: [node],
                                })
                              }
                            }

                            message.success('Updated Markup Rules')
                          })
                          .catch(e => {
                            message.error(e.message)
                            if (isAddingNew) {
                              gridRef.current?.api.applyTransaction({
                                remove: [node],
                              })
                            } else {
                              gridRef.current?.api.applyTransaction({
                                update: [first(markupRuleStackBeforeEdit)],
                              })
                            }
                          })
                          .finally(() => {
                            gridRef.current?.api.deselectAll()
                            setSelectedRows([])
                            gridRef.current?.api.hideOverlay()
                            setMarkupRuleStackBeforeEdit([])
                          })
                      }}
                    />
                  </GridWrapper>
                </EditingAgGridContext.Provider>
              </div>
            </Col>
          </Row>
        </Card>
      </Wrapper>
    </StyledLayout>
  )
}

const genColumns = (organizationsWithTopParent: Organization[]): (ColDef | ColGroupDef)[] => [
  {
    headerName: '#',
    colId: 'selectCol',
    checkboxSelection: true,
    headerCheckboxSelection: true,
    width: 80,
    resizable: false,
  },
  {
    headerName: 'Top Parent',
    field: 'topParentId',
    colId: 'topParentId',
    valueGetter: params => params.data?.topParent?.id || params.data?.topParentId,
    valueFormatter: params => {
      if (params.node?.group) {
        return (
          first(params.node?.allLeafChildren)?.data?.topParent?.name ||
          first(params.node?.allLeafChildren)?.data?.topParentId
        )
      }
      return params.data?.topParent?.name || params.data?.topParentId
    },
    valueSetter: params => {
      if (params.node?.group || params.newValue === params.oldValue) {
        return false
      }
      params.data.topParentId = params.newValue
      params.data.topParent = organizationsWithTopParent.find(i => i.id === params.newValue)
      return true
    },
    editable: true,
    cellEditor: AgFacilitySelector,
    cellEditorParams: {
      powerList: organizationsWithTopParent,
      topParentOnly: true,
    },
    enableRowGroup: true,
    filter: 'agSetColumnFilter',
    filterParams: {
      valueFormatter: ({ value }: ValueFormatterParams) => {
        return organizationsWithTopParent.find(i => i.id === value)?.name || value
      },
    },
  },
  {
    headerName: 'Facility',
    field: 'facilityId',
    colId: 'facilityId',
    valueGetter: params => params.data?.facility?.facilityId || params.data?.facilityId,
    valueFormatter: params => {
      if (params.node?.group) {
        return (
          first(params.node?.allLeafChildren)?.data?.facility?.name ||
          first(params.node?.allLeafChildren)?.data?.facilityId
        )
      }
      return params.data?.facility?.name || params.data?.facilityId
    },
    valueSetter: params => {
      if (params.node?.group || params.newValue === params.oldValue) {
        return false
      }
      params.data.facilityId = params.newValue
      params.data.facility = organizationsWithTopParent.find(i => i.facilityId === Number(params.newValue))
      return true
    },
    editable: true,
    cellEditor: AgFacilitySelector,
    cellEditorParams: {
      powerList: organizationsWithTopParent,
    },
    enableRowGroup: true,
    filter: 'agSetColumnFilter',
    filterParams: {
      valueFormatter: ({ value }: ValueFormatterParams) => {
        return organizationsWithTopParent.find(i => i.facilityId === Number(value))?.name || value
      },
    },
  },
  {
    headerName: 'Vendor',
    field: 'vendor',
    colId: 'vendor',
    editable: true,
    cellEditor: AgNormalizedSupplierSearch,
    comparator: stringCaseInsensitiveComparator,
    enableRowGroup: true,
    filter: true,
  },
  {
    headerName: 'Manufacturer',
    field: 'manufacturer',
    colId: 'manufacturer',
    editable: true,
    autoHeight: true,
    cellEditor: AgMultipleNormalizedManufacturerSearch,
    cellRenderer: ({ data }: { data?: MarkupRuleWithDetail }) => {
      return (
        <Space size={0} direction="vertical">
          {data?.manufacturer?.map(i => (
            <Typography.Text key={i}>{i}</Typography.Text>
          ))}
        </Space>
      )
    },
  },
  {
    headerName: 'UNSPSC (Commodity)',
    colId: 'unspsc',
    field: 'unspsc',
    filter: true,
    editable: true,
    valueGetter: ({ data }) => data?.unspsc,
    valueSetter: ({ node, data, newValue }) => {
      if (node && !node.group) {
        node.setData({
          ...data,
          unspsc: newValue?.map((i: LabeledValue) => i.value) || null,
          unspscCommodity:
            newValue?.map((i: LabeledValue) => ({
              commodity: i.value,
              commodityTitle: i.label,
            })) || null,
        })
        return true
      }
      return false
    },
    cellEditor: AgUnspscCommoditySelector,
    autoHeight: true,
    cellRenderer: ({ data }: { data?: MarkupRuleWithDetail }) => {
      return (
        <Space size={0} direction="vertical">
          {data?.unspscCommodity?.map(i => (
            <Tooltip key={i.commodity} placement="topLeft" title={`${i.commodity} | ${i.commodityTitle}`}>
              {`${i.commodity} | ${i.commodityTitle}`}
            </Tooltip>
          ))}
        </Space>
      )
    },
  },
  {
    headerName: 'Market Basket',
    field: 'marketBasket',
    colId: 'marketBasket',
    editable: true,
    autoHeight: true,
    valueGetter: ({ data }) => data?.marketBasket,
    valueSetter: ({ node, data, newValue }) => {
      if (node && !node.group) {
        node.setData({
          ...data,
          marketBasket: newValue?.map((i: LabeledValue) => i.value) || null,
          baskets:
            newValue?.map((i: LabeledValue) => ({
              id: i.value,
              name: i.label,
            })) || null,
        })
        return true
      }
      return false
    },
    cellEditor: AgMarketBasketSelector,
    cellRenderer: ({ data }: { data?: MarkupRuleWithDetail }) => {
      return (
        <Space size={0} direction="vertical">
          {data?.baskets?.map(i => (
            <Tooltip key={i.id} placement="topLeft" title={i.name}>
              <Typography.Text>{i.name}</Typography.Text>
            </Tooltip>
          ))}
        </Space>
      )
    },
  },
  {
    headerName: 'Markup Percentage',
    field: 'markupPercent',
    colId: 'markupPercent',
    valueFormatter: percentageFormatter,
    editable: true,
    cellEditor: 'floatEditor',
    pinned: 'right',
  },
]

const StyledLayout = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;

  .ant-page-header-heading-left {
    flex-basis: 40%;

    .ant-page-header-heading-title {
      width: 100%;
    }
  }

  .ant-page-header-heading-extra {
    max-width: 60%;
  }
`

const Wrapper = styled.div`
  padding: 0 ${props => props.theme.SpaceL} ${props => props.theme.SpaceL} ${props => props.theme.SpaceL};
  flex: 1;
  display: flex;
  flex-direction: column;
  padding-top: 1em;
`

const GridWrapper = styled.div`
  flex: 1;
  min-height: 500px;
  padding: .ag-header {
    background: ${props => props.theme.ColorNeutral30};
  }

  .ag-row-selected {
    background: ${props => props.theme.ColorBlue20};
  }

  .ag-row-hover {
    background: ${props => props.theme.ColorBlue10};
  }

  .ant-select-selector {
    width: 100% !important;
  }
`

export const EditingAgGridContext = React.createContext<{
  editingItem?: Partial<MarkupRuleWithDetail>
  onChangeEditingItem: Dispatch<SetStateAction<Partial<MarkupRuleWithDetail> | undefined>>
}>({ onChangeEditingItem: () => {} })
